import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SideMenu from "../../common/SideMenu";
import DashboardNav from "../component/DashboardNav";
import useSharedFuelData from "../../../hooks/useSharedFuelData";
import { Sparklines, SparklinesBars } from 'react-sparklines';
import { ToastContainer, toast } from "react-toastify";
import { 
  createIndicator,
  fetchAllIndicators,
  updatedIndicators,
  deleteIndicators,
  fetchAllFuels,
  createFuel,
  updateFuel,
  deleteFuel
} from "../../../utils/api/liveFeedApi";
import {
  SET_MACRO_INDICATORS,
  ADD_MACRO_INDICATOR,
  UPDATE_MACRO_INDICATOR,
  DELETE_MACRO_INDICATOR,
  CLEAR_MACRO_INDICATORS,
  SET_FUEL_DATA,
  ADD_FUEL,
  UPDATE_FUEL,
  DELETE_FUEL,
  CLEAR_FUEL_DATA,
} from "../../../store/actions/actiontypes";

// Color palette for sparklines
const SPARKLINE_COLORS = [
  '#007bff', '#28a745', '#dc3545', '#ffc107', '#17a2b8',
  '#6f42c1', '#e83e8c', '#fd7e14', '#20c997', '#6610f2',
  '#0dcaf0', '#198754', '#ff6b6b', '#4ecdc4', '#ffd166',
  '#06d6a0', '#118ab2', '#ef476f', '#073b4c', '#7209b7'
];

const LiveFeed = () => {
  const title = "Live Feeds";
  const dispatch = useDispatch();
  
  // Get state from Redux store
  const { macroIndicators: reduxIndicators, fuelData } = useSelector((state) => state.liveFeed);
  const token = JSON.parse(localStorage.getItem("eko-360-p2-token"));
  const { sharedFuelData, updateSharedFuelData } = useSharedFuelData();

  // Local UI state
  const [localIndicators, setLocalIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newIndicator, setNewIndicator] = useState({ name: '', label: '' });
  const [message, setMessage] = useState('');
  const [editingFuelId, setEditingFuelId] = useState(null);
  const [editedFuelData, setEditedFuelData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sparklineTooltipValue, setSparklineTooltipValue] = useState(null);
  const [sparklineTooltipPosition, setSparklineTooltipPosition] = useState({ x: 0, y: 0 });
  const [hoveredSparklineId, setHoveredSparklineId] = useState(null);
  const [newFuel, setNewFuel] = useState({
    type: '',
    price: '',
    link: '',
    data: ''
  });
  const [error, setError] = useState(null);


  // Helper function to get color for fuel
  const getColorForFuel = (fuelType, index) => {
    // Try to find existing fuel with same type to maintain color consistency
    const existingFuel = fuelData.find(f => f.fuelType === fuelType);
    if (existingFuel) {
      return existingFuel.sparklineColor;
    }
    
    // If not found, assign from palette based on index
    return SPARKLINE_COLORS[index % SPARKLINE_COLORS.length];
  };

  // Sync Redux state with local state
  useEffect(() => {
    if (reduxIndicators && reduxIndicators.length > 0) {
      setLocalIndicators(reduxIndicators);
      setLoading(false);
    }
  }, [reduxIndicators]);

  // Load indicators on mount
  useEffect(() => {
    const loadIndicators = async () => {
      try {
        setLoading(true);
        const data = await fetchAllIndicators(token);
        dispatch({ type: SET_MACRO_INDICATORS, payload: data });
      } catch (err) {
        setError("Failed to load indicators");
      } finally {
        setLoading(false);
      }
    };
    loadIndicators();
  }, [dispatch, token]);

  // Load fuels on mount
  useEffect(() => {
    const loadFuels = async () => {
      try {
        const fuels = await fetchAllFuels(token);
        
        // Transform API data to match expected format
        const transformedFuels = fuels.map((fuel, index) => {
          const currentPrice = typeof fuel.price === 'string' && fuel.price.startsWith('$') 
            ? parseFloat(fuel.price.substring(1)) 
            : parseFloat(fuel.price) || 0;
          
          // Get color for this fuel
          const sparklineColor = getColorForFuel(fuel.type, index);
          
          return {
            id: fuel.id,
            uuid: fuel.uuid,
            fuelType: fuel.type,
            price: currentPrice,
            link: fuel.link,
            tooltipData: fuel.data,
            sparklineData: Array.from({length: 20}, () => 
              Math.random() * 50 + currentPrice - 25
            ),
            sparklineColor: sparklineColor
          };
        });
        
        dispatch({ type: SET_FUEL_DATA, payload: transformedFuels });
      } catch (err) {
        console.error("Error fetching fuels:", err);
        setError("Failed to load fuel data");
      }
    };
    
    loadFuels();
  }, [dispatch, token]);

  // API and Redux sync functions
  const saveIndicator = async (indicator) => {
    try {
      let result;
      if (indicator.uuid) {
        result = await updatedIndicators(indicator, indicator.uuid, token);
        dispatch({ type: UPDATE_MACRO_INDICATOR, payload: result });
        toast.success(`Indicator "${indicator.label}" updated successfully!`);
      } else {
        result = await createIndicator(indicator, token);
        dispatch({ type: ADD_MACRO_INDICATOR, payload: result });
        toast.success(`Indicator "${indicator.label}" added successfully!`);
      }
      return result;
    } catch (error) {
      const action = indicator.uuid ? "updating" : "creating";
      toast.error(`Error ${action} indicator: ${error.message}`);
      throw error;
    }
  };

  // Handle form submission
  const handleIndicatorsSubmit = async (e) => {
    e.preventDefault();
    try {
      toast.info("Updating indicators...", { autoClose: false });
      const savePromises = localIndicators.map(ind => saveIndicator(ind));
      await Promise.all(savePromises);
      toast.dismiss();
      toast.success("All indicators updated successfully!");
    } catch (error) {
      toast.dismiss();
      toast.error(`Error saving indicators: ${error.message}`);
    }
  };

  const handleIndicatorChange = (uuid, field, value) => {
    const updated = localIndicators.map(ind => 
      ind.uuid === uuid ? {...ind, [field]: value} : ind
    );
    setLocalIndicators(updated);
  };

  const handleAddIndicator = async (e) => {
    e.preventDefault();
    if (!newIndicator.name || !newIndicator.value) {
      toast.warning("Both name and value are required");
      return;
    }

    try {
      setLoading(true);
      toast.info("Adding new indicator...");
      const newInd = {
        name: newIndicator.name,
        value: newIndicator.value,
        label: newIndicator.name
      };
      
      const created = await saveIndicator(newInd);
      setLocalIndicators([...localIndicators, created]);
      setNewIndicator({ name: '', value: '', label: '' });
    } catch (error) {
      toast.error(error.message || "Failed to add indicator");
    } finally {
      setLoading(false);
    }
  };

  const removeIndicator = async (uuid) => {
    try {
      const indicator = localIndicators.find(ind => ind.uuid === uuid);
      toast.info(`Removing ${indicator?.label || 'indicator'}...`);
      await deleteIndicators(uuid, token);
      setLocalIndicators(localIndicators.filter(ind => ind.uuid !== uuid));
      dispatch({ type: DELETE_MACRO_INDICATOR, payload: { uuid } });
      toast.success(`"${indicator?.label || 'Indicator'}" removed successfully`);
    } catch (error) {
      toast.error("Failed to remove indicator");
    }
  };

  const clearAllIndicators = async () => {
    if (!window.confirm('Clear all indicators?')) return;
    
    try {
      toast.info("Clearing all indicators...", { autoClose: false });
      await Promise.all(localIndicators.map(ind => deleteIndicators(ind.uuid, token)));
      setLocalIndicators([]);
      dispatch({ type: CLEAR_MACRO_INDICATORS });
      toast.dismiss();
      toast.success("All indicators cleared successfully");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to clear indicators");
    }
  };

  // Fuel functions
  const handleEditedFuelChange = (e) => {
    const { name, value } = e.target;
    setEditedFuelData(prev => ({ ...prev, [name]: value }));
  };

  const saveEditedFuel = async () => {
    if (editingFuelId !== null) {
      try {
        toast.info("Updating fuel price...");
        
        // Find the original fuel to get its UUID
        const originalFuel = fuelData.find(f => f.id === editingFuelId);
        
        if (!originalFuel || !originalFuel.uuid) {
          throw new Error("Cannot update fuel: missing UUID");
        }
        
        const updatedFuelData = {
          type: editedFuelData.fuelType,
          price: editedFuelData.price.toString(),
          link: editedFuelData.link,
          data: editedFuelData.tooltipData
        };
        
        const updatedFuel = await updateFuel(updatedFuelData, originalFuel.uuid, token);
        
        // Update sparkline data (client-side only)
        const updatedFuelWithSparkline = {
          ...updatedFuel,
          fuelType: updatedFuel.type,
          price: typeof updatedFuel.price === 'string' && updatedFuel.price.startsWith('$') 
            ? parseFloat(updatedFuel.price.substring(1)) 
            : parseFloat(updatedFuel.price) || 0,
          tooltipData: updatedFuel.data,
          sparklineData: [
            ...(fuelData.find(f => f.uuid === updatedFuel.uuid)?.sparklineData || []),
            parseFloat(updatedFuelData.price) || 0
          ].slice(-20),
          sparklineColor: originalFuel.sparklineColor
        };
        
        dispatch({ type: UPDATE_FUEL, payload: updatedFuelWithSparkline });
        setIsModalOpen(false);
        toast.success("Fuel price updated successfully!");
      } catch (error) {
        console.error("Error updating fuel:", error);
        toast.error(`Failed to update fuel: ${error.message}`);
      }
    }
  };

  const startEditingFuel = (fuel) => {
    setEditingFuelId(fuel.id);
    setEditedFuelData({ 
      fuelType: fuel.fuelType || fuel.type, 
      price: fuel.price,
      link: fuel.link,
      tooltipData: fuel.tooltipData || fuel.data
    });
    setIsModalOpen(true);
  };

  const handleDeleteFuel = async (uuid) => {
    try {
      const fuelToDelete = fuelData.find(f => f.uuid === uuid);
      toast.info(`Deleting ${fuelToDelete?.fuelType || 'fuel'}...`);
      
      await deleteFuel(uuid, token);
      dispatch({ type: DELETE_FUEL, payload: { uuid } });
      toast.success("Fuel deleted successfully!");
    } catch (error) {
      console.error("Error deleting fuel:", error);
      toast.error(`Failed to delete fuel: ${error.message}`);
    }
  };

  const handleAddFuel = async (e) => {
    e.preventDefault();
    
    if (!newFuel.type || !newFuel.price) {
      toast.warning("Fuel type and price are required");
      return;
    }

    try {
      toast.info("Adding new fuel...");
      const createdFuel = await createFuel(newFuel, token);
      
      // Get color for this fuel type
      const sparklineColor = getColorForFuel(newFuel.type, fuelData.length);
      
      // Add client-side properties
      const fuelWithSparkline = {
        ...createdFuel,
        id: createdFuel.id,
        fuelType: createdFuel.type,
        price: typeof createdFuel.price === 'string' && createdFuel.price.startsWith('$') 
          ? parseFloat(createdFuel.price.substring(1)) 
          : parseFloat(createdFuel.price) || 0,
        tooltipData: createdFuel.data,
        sparklineData: Array.from({length: 20}, () => 
          Math.random() * 50 + (typeof createdFuel.price === 'string' && createdFuel.price.startsWith('$') 
            ? parseFloat(createdFuel.price.substring(1)) 
            : parseFloat(createdFuel.price) || 0) - 25
        ),
        sparklineColor: sparklineColor
      };
      
      dispatch({ type: ADD_FUEL, payload: fuelWithSparkline });
      setNewFuel({ type: '', price: '', link: '', data: '' });
      toast.success("Fuel added successfully!");
    } catch (error) {
      console.error("Error adding fuel:", error);
      toast.error(`Failed to add fuel: ${error.message}`);
    }
  };

  // Sparkline tooltip handlers
  const handleSparklineMouseMove = (e, fuelId, sparklineData) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const index = Math.floor((e.clientX - rect.left) / rect.width * sparklineData.length);
    if (index >= 0 && index < sparklineData.length) {
      setSparklineTooltipValue(sparklineData[index].toFixed(2));
      setSparklineTooltipPosition({ x: e.clientX, y: e.clientY });
      setHoveredSparklineId(fuelId);
    }
  };

  const handleSparklineMouseLeave = () => {
    setSparklineTooltipValue(null);
    setHoveredSparklineId(null);
  };

  return (
    <div className="member body">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-2">
            <SideMenu />
          </div>
          <div className="col-md-10">
            <DashboardNav page={title} />
            <div>
              <div className="__house">
                <div className="__btcard">
                  <div className="__btcardin">
                    {/* Indicators Section */}
                    <div className="row">
                      <div className="col-lg-2 col-md-2 col-sm-2"></div>
                      <div className="col-lg-8 col-md-8 col-sm-8">
                        <marquee className="mb-3" style={{color: "#000", fontSize: "18px", padding: "5px 0"}}>
                          {localIndicators.map(indicator => (
                            <span key={indicator.uuid} className="mx-3">
                              {indicator.name}: {indicator.value || 'N/A'}
                            </span>
                          ))}
                        </marquee>

                        <form onSubmit={handleIndicatorsSubmit}>
                          <div className="row" style={{maxHeight: '20rem', overflowY: 'auto'}}>
                            {localIndicators.map(indicator => (
                              <div key={indicator.uuid} className="col-md-6 mb-3">
                                <div className="p-3 border rounded h-100">
                                  <div className="d-flex justify-content-between align-items-center mb-2">
                                    <input
                                      type="text"
                                      value={indicator.name}
                                      onChange={(e) => handleIndicatorChange(indicator.uuid, 'name', e.target.value)}
                                      className="form-control __regdivform"
                                      style={{ width: '60%' }}
                                      placeholder={`Enter ${indicator.name} value`}
                                    />
                                    <button 
                                      type="button" 
                                      className="btn btn-sm btn-danger ml-2"
                                      onClick={() => removeIndicator(indicator.uuid)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <input
                                    type="text"
                                    value={indicator.value}
                                    onChange={(e) => handleIndicatorChange(indicator.uuid, 'value', e.target.value)}
                                    className="form-control __regdivform"
                                    placeholder={`Enter ${indicator.name} value`}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="border-top pt-3 mt-3">
                            <div className="form-group">
                              <label className="__modlab">Add New Indicator</label>
                              <div className="row">
                                <div className="col-md-5">
                                  <input
                                    name="name"
                                    type="text"
                                    placeholder="Name (e.g. Unemployment)"
                                    value={newIndicator.name}
                                    onChange={(e) => setNewIndicator({...newIndicator, name: e.target.value})}
                                    className="form-control __regdivform"
                                  />
                                </div>
                                <div className="col-md-5">
                                  <input
                                    name="value"
                                    type="text"
                                    placeholder="Value (e.g. 5.2%)"
                                    value={newIndicator.value}
                                    onChange={(e) => setNewIndicator({...newIndicator, value: e.target.value})}
                                    className="form-control __regdivform"
                                  />
                                </div>
                                <div className="col-md-2">
                                  <button 
                                    onClick={handleAddIndicator} 
                                    className="btn btn-block __autbtn"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between mt-3">
                            <button type="submit" className="btn __autbtn">
                              Update Indicators
                            </button>
                            {localIndicators.length > 0 && (
                              <button 
                                type="button" 
                                className="btn btn-danger"
                                onClick={clearAllIndicators}
                              >
                                Clear All
                              </button>
                            )}
                          </div>
                        </form>

                        {message && (
                          <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-danger'} mt-3`}>
                            {message}
                          </div>
                        )}
                      </div>
                      <div className="col-lg-2 col-md-2 col-sm-2"></div>
                    </div>

                    <hr className="my-4" />

                    {/* Fuel Price Management */}
                    <div className="row">
                      <div className="col-12">
                        <h4>Manage Fuel Price Cards</h4>
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Fuel Type</th>
                              <th>Price</th>
                              <th>Link</th>
                              <th>Tooltip Data</th>
                              <th>Sparklines</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fuelData.map((fuel) => (
                              <tr key={fuel.id}>
                                <td>{fuel.fuelType}</td>
                                <td>₦{fuel.price.toFixed(2)}</td>
                                <td>
                                  <a href={fuel.link} target="_blank" rel="noopener noreferrer">
                                    {fuel.link}
                                  </a>
                                </td>
                                <td>{fuel.tooltipData}</td>
                                <td style={{ position: 'relative' }}>
                                  {/* {console.log('Fuel sparkline data:', fuel.sparklineData)} */}
                                  {fuel.sparklineData && fuel.sparklineData.length > 1 ? (
                                    <div
                                      onMouseMove={(e) => handleSparklineMouseMove(e, fuel.id, fuel.sparklineData)}
                                      onMouseLeave={handleSparklineMouseLeave}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <Sparklines
                                        data={fuel.sparklineData}
                                        width={120}
                                        height={40}
                                        margin={2}
                                      >
                                        <SparklinesBars
                                          style={{ 
                                            fill: fuel.sparklineColor, 
                                            stroke: fuel.sparklineColor, 
                                            strokeWidth: 1 
                                          }}
                                        />
                                      </Sparklines>
                                    </div>
                                  ) : (
                                    <div style={{ width: 120, height: 40, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <span style={{ fontSize: '10px', color: '#999' }}>No data</span>
                                    </div>
                                  )}
                                  {sparklineTooltipValue !== null && hoveredSparklineId === fuel.id && (
                                    <div
                                      style={{
                                        position: 'fixed',
                                        top: sparklineTooltipPosition.y + 10,
                                        left: sparklineTooltipPosition.x + 10,
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        color: 'white',
                                        padding: '3px 6px',
                                        borderRadius: '3px',
                                        pointerEvents: 'none',
                                        zIndex: 1000,
                                      }}
                                    >
                                      ₦{sparklineTooltipValue}
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-primary mr-2"
                                    onClick={() => startEditingFuel(fuel)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteFuel(fuel.uuid)}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Add New Fuel Form */}
                        <div className="row mt-4">
                          <div className="col-12">
                            <h4>Add New Fuel Type</h4>
                            <form onSubmit={handleAddFuel} className="row">
                              <div className="col-md-3">
                                <input
                                  type="text"
                                  placeholder="Fuel Type"
                                  value={newFuel.type}
                                  onChange={(e) => setNewFuel({...newFuel, type: e.target.value})}
                                  className="form-control"
                                  required
                                />
                              </div>
                              <div className="col-md-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  placeholder="Price"
                                  value={newFuel.price}
                                  onChange={(e) => setNewFuel({...newFuel, price: e.target.value})}
                                  className="form-control"
                                  required
                                />
                              </div>
                              <div className="col-md-3">
                                <input
                                  type="text"
                                  placeholder="Link"
                                  value={newFuel.link}
                                  onChange={(e) => setNewFuel({...newFuel, link: e.target.value})}
                                  className="form-control"
                                  required
                                />
                              </div>
                              <div className="col-md-3">
                                <input
                                  type="text"
                                  placeholder="Tooltip Data"
                                  value={newFuel.data}
                                  onChange={(e) => setNewFuel({...newFuel, data: e.target.value})}
                                  className="form-control"
                                  required
                                />
                              </div>
                              <div className="col-md-1">
                                <button type="submit" className="btn btn-success">
                                  Add
                                </button>
                              </div>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fuel Edit Modal */}
                    {isModalOpen && (
                      <div className="modal-overlay">
                        <div className="modal-content">
                          <h4>Edit Fuel Price Card</h4>
                          <form>
                            <div className="form-group">
                              <label className="__modlab">Price</label>
                              <input
                                type="number"
                                name="price"
                                value={editedFuelData.price}
                                onChange={handleEditedFuelChange}
                                className="form-control __regdivform"
                                step="0.01"
                              />
                            </div>
                            <div className="form-group">
                              <label className="__modlab">Fuel Type</label>
                              <input
                                type="text"
                                name="fuelType"
                                value={editedFuelData.fuelType}
                                onChange={handleEditedFuelChange}
                                className="form-control __regdivform"
                              />
                            </div>
                            <div className="form-group">
                              <label className="__modlab">Link</label>
                              <input
                                type="text"
                                name="link"
                                value={editedFuelData.link}
                                onChange={handleEditedFuelChange}
                                className="form-control __regdivform"
                              />
                            </div>
                            <div className="form-group">
                              <label className="__modlab">Tooltip Data</label>
                              <input
                                type="text"
                                name="tooltipData"
                                value={editedFuelData.tooltipData}
                                onChange={handleEditedFuelChange}
                                className="form-control __regdivform"
                              />
                            </div>
                            <br />
                            <button
                              type="button"
                              className="btn btn-success mr-2"
                              onClick={saveEditedFuel}
                            >
                              Save Changes
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => setIsModalOpen(false)}
                            >
                              Cancel
                            </button>
                          </form>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="__spacet7p"></div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default LiveFeed;